<template>
  <div
    ref="container"
    class="absolute inset-x-0 inset-y-[6px] pointer-events-none"
  >
    <div
      v-for="m in matches"
      :key="m.id"
      data-timeline-match
      class="absolute top-0 bottom-0 rounded-[3px] border-l-2 pointer-events-auto cursor-pointer transition-[opacity,filter] duration-150"
      :style="styleFor(m)"
      @pointerenter="onMove"
      @pointermove="onMove"
      @pointerleave="onLeave"
      @pointerdown="onTapStart"
      @pointerup="onTapEnd"
      @pointercancel="onTapCancel"
    >
      <span class="absolute inset-y-0 -left-1 -right-1" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, ref } from 'vue'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import {
  canHover,
  TimelineHoverKey,
  type TimelineSideMatch,
} from './timeline-types'

const props = defineProps<{
  matches: readonly TimelineSideMatch[]
  duration: number
}>()

const hover = inject(TimelineHoverKey, null)
const container = ref<HTMLElement>()

const PALETTE: Record<ScoreLevel, { bg: string; border: string }> = {
  [ScoreLevel.High]: { bg: 'rgba(220, 60, 63, 0.15)', border: '#dc3c3f' },
  [ScoreLevel.Medium]: { bg: 'rgba(245, 185, 88, 0.15)', border: '#f5b958' },
  [ScoreLevel.Low]: { bg: 'rgba(52, 177, 199, 0.15)', border: '#34b1c7' },
}

const MIN_WIDTH = 6

const styleFor = (m: TimelineSideMatch) => {
  const dur = props.duration || 1
  const start = Math.max(0, (m.start / dur) * 100)
  const end = Math.min(100, (m.end / dur) * 100)
  const width = Math.max(0, end - start)
  const colors = PALETTE[m.level] ?? PALETTE[ScoreLevel.High]
  const id = hover?.hoveredId.value
  const active = id === m.id
  const dimmed = id && !active

  return {
    left: `min(${start}%, calc(100% - ${MIN_WIDTH}px))`,
    width: `${width}%`,
    minWidth: `${MIN_WIDTH}px`,
    backgroundColor: colors.bg,
    borderColor: colors.border,
    opacity: dimmed ? 0.25 : 1,
    filter: dimmed ? 'grayscale(1)' : 'none',
    zIndex: active ? 2 : 1,
  }
}

const HIT_PADDING = 4

const matchAt = (clientX: number): TimelineSideMatch | null => {
  const el = container.value
  if (!el) return null
  const rect = el.getBoundingClientRect()
  if (rect.width <= 0) return null

  const dur = props.duration || 1
  const x = clientX - rect.left

  let best: TimelineSideMatch | null = null
  let bestDist = Infinity
  for (const m of props.matches) {
    const startX = Math.min(
      (m.start / dur) * rect.width,
      Math.max(0, rect.width - MIN_WIDTH)
    )
    const endX = Math.max((m.end / dur) * rect.width, startX + MIN_WIDTH)
    const dist = x < startX ? startX - x : x > endX ? x - endX : 0
    if (dist > HIT_PADDING) continue
    if (
      dist < bestDist ||
      (dist === bestDist && best && m.start > best.start)
    ) {
      best = m
      bestDist = dist
    }
  }
  return best
}

const anchorFor = (el: HTMLElement, clientX: number) => {
  const rect = el.getBoundingClientRect()
  return { x: clientX, top: rect.top, bottom: rect.bottom }
}

const isHoverPointer = (e: PointerEvent) =>
  e.pointerType === 'mouse' && canHover()

const onMove = (e: PointerEvent) => {
  if (!isHoverPointer(e)) return
  const el = container.value
  const m = matchAt(e.clientX)
  if (!el || !m) {
    hover?.hide()
    return
  }
  hover?.enter(m.match, anchorFor(el, e.clientX))
}

const onLeave = (e: PointerEvent) => {
  if (!isHoverPointer(e)) return
  hover?.hide()
}

const TAP_SLOP = 8

let tapStart: { id: number; x: number; y: number } | null = null

const onTapStart = (e: PointerEvent) => {
  if (e.pointerType === 'mouse') return
  tapStart = { id: e.pointerId, x: e.clientX, y: e.clientY }
}

const onTapCancel = () => {
  tapStart = null
}

const onTapEnd = (e: PointerEvent) => {
  if (e.pointerType === 'mouse') return
  const start = tapStart
  tapStart = null
  if (!start || start.id !== e.pointerId) return
  if (
    Math.abs(e.clientX - start.x) > TAP_SLOP ||
    Math.abs(e.clientY - start.y) > TAP_SLOP
  )
    return

  const el = container.value
  const m = matchAt(e.clientX)
  if (!el || !m) return
  if (hover?.hoveredId.value === m.id) {
    hover.hide()
    return
  }
  hover?.enter(m.match, anchorFor(el, e.clientX), { sticky: true })
}
</script>
